import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import type { Editor } from '@tiptap/react';
import {
  ChevronDown, CodeXml, Eye, Loader2, Palette, Maximize2, Minimize2, Minus, Paperclip, RotateCcw, Send, Sparkles, Square, Trash2, Type, Wand2, Workflow, X,
} from 'lucide-react';
import { domainOf, formatBytes, htmlToPlain, plainToHtml, splitSubject, type DraftMode } from '@dts/core';
import { useUi, type ComposerState } from '@/lib/store';
import { api, errorMessage } from '@/lib/api';
import { useMe, usePrefs, qk } from '@/lib/queries';
import { brandedHtml, sendWithUndo, signatureFor, toPayload } from '@/lib/compose';
import { TemplateDesignerModal, useTemplateFor } from './TemplateDesigner';
import { fileToBase64 } from '@/lib/format';
import { queryClient } from '@/lib/query-client';
import { Favicon } from './Avatar';
import { RecipientInput } from './RecipientInput';
import { EditorToolbar, HtmlSourceEditor, REWRITES, RichEditor } from './RichEditor';
import { confirmDialog } from './Dialogs';
import { Popover, MenuItem } from './Reader';
import { Button, cn, IconButton, Modal } from './ui';

const TONES = ['Professional', 'Friendly', 'Concise', 'Persuasive', 'Apologetic', 'Enthusiastic'];
const MAX_ATTACH = 50 * 1024 * 1024;
// Bounds the send request (base64 adds a third); the server turns anything past ~10 MB into download links.
const MAX_ATTACH_TOTAL = 60 * 1024 * 1024;
const LINK_THRESHOLD = 7 * 1024 * 1024;
/** Files over this offer to go out as a download link instead, uploaded to the user's own DTS Mail storage. */
const UPLOAD_LINK_THRESHOLD = 4 * 1024 * 1024;
const UPLOAD_LINK_MAX = 1024 * 1024 * 1024;

/** Puts a block at the end of the message body, above the signature. */
function insertAboveSignature(html: string, block: string): string {
  const i = html.indexOf('<div data-signature="1">');
  return i < 0 ? html + block : html.slice(0, i) + block + html.slice(i);
}

function downloadLinkHtml(f: { url: string; name: string; size: number; expires: string }): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const until = f.expires ? ` · available until ${new Date(f.expires).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : '';
  return `<p>📎 <a href="${esc(f.url)}">${esc(f.name)}</a> (${formatBytes(f.size)}${until})</p>`;
}

function stripSignature(html: string): string {
  return html.replace(/<div data-signature="1">[\s\S]*$/, '');
}

export function ComposerDock() {
  const composers = useUi((s) => s.composers);
  const maximized = composers.find((c) => c.maximized && !c.minimized);
  return (
    <>
      <AnimatePresence>
        {maximized && (
          <motion.div key="backdrop" className="fixed inset-0 z-40 bg-black/35 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {composers.filter((c) => !c.minimized).map((c, i) => (
          <Composer key={c.id} c={c} index={i} />
        ))}
      </AnimatePresence>
      <div className="pointer-events-none fixed bottom-0 right-5 z-40 flex items-end gap-3">
        <AnimatePresence>
          {composers.filter((c) => c.minimized).map((c, i) => (
            <Composer key={c.id} c={c} index={i} />
          ))}
        </AnimatePresence>
      </div>
    </>
  );
}

const MARGIN = 10;

function composerSize() {
  const w = Math.min(600, window.innerWidth - MARGIN * 2);
  const h = Math.min(660, Math.round(window.innerHeight * 0.84), window.innerHeight - MARGIN * 2);
  return { w, h };
}

/** Keeps the composer fully inside the app window. */
function clampPos(p: { x: number; y: number }, w: number, h: number) {
  return {
    x: Math.min(Math.max(MARGIN, p.x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
    y: Math.min(Math.max(MARGIN, p.y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
  };
}

/** Floating window position: starts docked bottom-right, can be dragged by its title bar, re-clamped on resize. */
function useFloatingWindow(index: number) {
  const [size, setSize] = useState(composerSize);
  const [pos, setPos] = useState(() => {
    const { w, h } = composerSize();
    return clampPos({ x: window.innerWidth - w - 20 - index * 28, y: window.innerHeight - h - MARGIN - index * 28 }, w, h);
  });
  useEffect(() => {
    const onResize = () => {
      const next = composerSize();
      setSize(next);
      setPos((p) => clampPos(p, next.w, next.h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button,input,a')) return;
    const start = { mx: e.clientX, my: e.clientY, x: pos.x, y: pos.y };
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => setPos(clampPos({ x: start.x + ev.clientX - start.mx, y: start.y + ev.clientY - start.my }, size.w, size.h));
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };
  return { pos, size, onPointerDown };
}

function Composer({ c, index }: { c: ComposerState; index: number }) {
  const update = useUi((s) => s.updateComposer);
  const close = useUi((s) => s.closeComposer);
  const floating = useFloatingWindow(index);
  const me = useMe().data;
  const prefs = usePrefs().data;
  const [editor, setEditor] = useState<Editor | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [tone, setTone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [undoHtml, setUndoHtml] = useState<string | null>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [selectionResult, setSelectionResult] = useState<{ text: string; busy: boolean; from: number; to: number } | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [designOpen, setDesignOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const aiInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const autoRan = useRef(false);
  const lastSaved = useRef('');

  const identities = me?.addresses.filter((a) => a.enabled) ?? [];
  const from = c.from ?? identities[0]?.email;
  const template = useTemplateFor(from);
  const isReply = c.mode === 'reply' || c.mode === 'replyAll' || c.mode === 'forward';
  const title = c.subject || (c.mode === 'reply' || c.mode === 'replyAll' ? 'Reply' : c.mode === 'forward' ? 'Forward' : 'New message');

  // ---------- AI drafting ----------
  const runAi = useCallback(
    async (instruction: string, override?: { mode?: DraftMode; rewriteHow?: string }) => {
      if (!editor && !c.htmlMode) return;
      const current = c.htmlMode || !editor ? c.html : editor.getHTML();
      const write = (h: string) => (c.htmlMode || !editor ? update(c.id, { html: h }) : editor.commands.setContent(h, false));
      setUndoHtml(current);
      setBusy(true);
      const sig = signatureFor(from);
      const sigHtml = sig ? `<p></p><div data-signature="1">${sig}</div>` : '';
      const existing = htmlToPlain(stripSignature(current)).trim();
      const mode: DraftMode = override?.mode ?? (c.mode === 'reply' ? 'reply' : c.mode === 'replyAll' ? 'replyAll' : c.mode === 'forward' ? 'forward' : 'compose');
      abort.current = new AbortController();
      let frame = 0;
      let latest = '';
      const paint = () => {
        frame = 0;
        let body = latest;
        if (mode === 'compose') {
          const parts = splitSubject(latest);
          if (parts.subject !== undefined) {
            body = parts.body;
            if (!c.subject) update(c.id, { subject: parts.subject });
          } else if (/^\s*s(u(b(j(e(c(t)?)?)?)?)?)?$/i.test(latest)) body = '';
        }
        write(plainToHtml(body) + sigHtml);
      };
      try {
        const full = await api.aiDraft(
          {
            mode,
            instruction: override?.rewriteHow ?? instruction,
            messageId: c.sourceId,
            draft: existing || undefined,
            tone: tone ?? undefined,
            to: c.to,
            from,
            subject: mode === 'compose' ? c.subject || undefined : undefined,
          },
          (_chunk, text) => {
            latest = text;
            if (!frame) frame = requestAnimationFrame(paint);
          },
          abort.current.signal,
        );
        latest = full;
        cancelAnimationFrame(frame);
        paint();
        if (!c.htmlMode && editor) update(c.id, { html: editor.getHTML() });
      } catch (e) {
        if (!abort.current?.signal.aborted) {
          toast.error(`AI draft failed: ${errorMessage(e)}`);
          write(current);
        }
      } finally {
        setBusy(false);
        abort.current = null;
      }
    },
    [editor, c.id, c.mode, c.sourceId, c.subject, c.to, c.html, c.htmlMode, from, tone, update],
  );

  const rewriteSelection = async (selection: string, instruction: string) => {
    if (!editor || !selection.trim()) return;
    const { from: a, to: b } = editor.state.selection;
    setSelectionResult({ text: '', busy: true, from: a, to: b });
    try {
      const out = await api.aiDraft(
        { mode: 'rewrite', draft: htmlToPlain(stripSignature(editor.getHTML())), selection, instruction, messageId: c.sourceId },
        (_x, full) => setSelectionResult((s) => (s ? { ...s, text: full } : s)),
      );
      setSelectionResult((s) => (s ? { ...s, text: out.trim(), busy: false } : s));
    } catch (e) {
      toast.error(errorMessage(e));
      setSelectionResult(null);
    }
  };

  // Auto-run an instruction handed to the composer (smart reply chips, "Draft reply", Compose with AI).
  useEffect(() => {
    if ((editor || c.htmlMode) && c.autoAi && !autoRan.current) {
      autoRan.current = true;
      void runAi(c.autoAi);
    }
  }, [editor, c.autoAi, runAi]);

  useEffect(() => {
    if (c.aiFirst) setTimeout(() => aiInput.current?.focus(), 250);
  }, [c.aiFirst]);

  // ---------- draft autosave ----------
  useEffect(() => {
    const payload = toPayload({ ...c, attachments: c.attachments.filter((a) => a.size < 2 * 1024 * 1024) });
    const meaningful = htmlToPlain(stripSignature(c.html)).trim().length > 0 || c.subject.trim().length > 0;
    const sig = JSON.stringify([payload.to, payload.cc, payload.bcc, payload.subject, payload.html, payload.from]);
    if (!meaningful || busy || sig === lastSaved.current) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.saveDraft({ ...payload, draftId: undefined }, c.draftId);
        lastSaved.current = sig;
        setSavedAt(Date.now());
        if (!c.draftId) update(c.id, { draftId: r.id });
        queryClient.invalidateQueries({ queryKey: qk.folders });
      } catch {
        /* retry on next change */
      }
    }, 3500);
    return () => clearTimeout(t);
  }, [c, busy, update]);

  // ---------- attachments ----------
  /** Uploads to the user's own DTS Mail storage and puts a download link in the body; a chip shows it's on its way. */
  const sendAsLink = async (f: File) => {
    const id = `${f.name}-${f.size}-${Date.now()}`;
    update(c.id, (s) => ({ attachments: [...s.attachments, { id, fileName: f.name, contentType: f.type || 'application/octet-stream', size: f.size, loading: true }] }));
    const r = await window.dts.uploads.upload(f.name, f.type, new Uint8Array(await f.arrayBuffer()));
    update(c.id, (s) => ({ attachments: s.attachments.filter((a) => a.id !== id) }));
    if (!r.ok) {
      toast.error(`Couldn't upload ${f.name}`, { description: r.error });
      return false;
    }
    const block = downloadLinkHtml(r.data);
    if (c.htmlMode || !editor) update(c.id, (s) => ({ html: insertAboveSignature(s.html, block) }));
    else editor.commands.setContent(insertAboveSignature(editor.getHTML(), block), true);
    toast.success(`Added a download link for ${f.name}`);
    return true;
  };

  const addFiles = async (files: FileList | File[]) => {
    let total = c.attachments.reduce((n, a) => n + a.size, 0);
    for (const f of Array.from(files)) {
      if (f.size > UPLOAD_LINK_THRESHOLD && f.size <= UPLOAD_LINK_MAX && (await window.dts.uploads.available())) {
        const asLink = await confirmDialog(
          `Send ${f.name} as a download link?`,
          `It's ${formatBytes(f.size)}. Upload it to your own DTS Mail storage and put a download link in the email instead of attaching it? The link works for a year.`,
          { confirmLabel: 'Upload & add link', cancelLabel: 'Attach instead' },
        );
        if (asLink) {
          if (await sendAsLink(f)) continue;
          if (!(await confirmDialog(`Attach ${f.name} instead?`, undefined, { confirmLabel: 'Attach' }))) continue;
        }
      }
      if (f.size > MAX_ATTACH) {
        toast.error(`${f.name} is larger than 50 MB`);
        continue;
      }
      if (total + f.size > MAX_ATTACH_TOTAL) {
        toast.error(`${f.name} would put this message over 60 MB of attachments`);
        continue;
      }
      total += f.size;
      const id = `${f.name}-${f.size}-${Date.now()}`;
      update(c.id, (s) => ({ attachments: [...s.attachments, { id, fileName: f.name, contentType: f.type || 'application/octet-stream', size: f.size, loading: true }] }));
      const b64 = await fileToBase64(f);
      update(c.id, (s) => ({ attachments: s.attachments.map((a) => (a.id === id ? { ...a, base64: b64, loading: false } : a)) }));
    }
  };

  const send = () => {
    if (c.to.length + c.cc.length + c.bcc.length === 0) {
      toast.error('Add at least one recipient');
      return;
    }
    if (c.attachments.some((a) => a.loading)) {
      toast.error('Attachments are still loading');
      return;
    }
    const body = htmlToPlain(stripSignature(c.html));
    if (/\battach(ed|ment|ing)\b/i.test(body) && c.attachments.length === 0) {
      void confirmDialog('Send without an attachment?', 'Your message mentions an attachment but nothing is attached.', { confirmLabel: 'Send anyway' }).then((ok) => ok && sendWithUndo({ ...c, from }));
      return;
    }
    sendWithUndo({ ...c, from });
  };

  const discard = async () => {
    abort.current?.abort();
    close(c.id);
    if (c.draftId) {
      await api.deleteMessage(c.draftId, true).catch(() => undefined);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: qk.folders });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape' && !busy && !selectionResult) {
      update(c.id, { minimized: true, maximized: false });
    }
  };

  // ---------- minimized pill ----------
  if (c.minimized) {
    return (
      <motion.button
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        onClick={() => useUi.setState((s) => ({ composers: s.composers.map((x) => ({ ...x, minimized: x.id !== c.id })) }))}
        className="pointer-events-auto mb-0 flex h-11 w-[260px] items-center gap-2 rounded-t-2xl border border-line bg-raised px-4 text-left shadow-2xl"
      >
        {busy ? <Loader2 className="size-4 animate-spin text-accent" /> : <Type className="size-4 text-muted" />}
        <span className="flex-1 truncate text-[13px] font-medium">{title}</span>
        <X className="size-4 text-faint hover:text-fg" onClick={(e) => (e.stopPropagation(), close(c.id))} />
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 380, damping: 34 }}
      onKeyDown={onKeyDown}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={(e) => {
        if (e.dataTransfer.files.length) {
          e.preventDefault();
          setDragging(false);
          void addFiles(e.dataTransfer.files);
        }
      }}
      style={c.maximized ? undefined : { left: floating.pos.x, top: floating.pos.y, width: floating.size.w, height: floating.size.h }}
      className={cn(
        // Solid, not .glass: its unlayered background beats Tailwind's bg-* and left the editor see-through.
        'pointer-events-auto fixed flex flex-col overflow-hidden rounded-3xl border border-line bg-raised shadow-[0_30px_80px_-20px_rgba(0,0,0,.55)]',
        c.maximized ? 'inset-x-[8vw] bottom-[6vh] top-[6vh] z-50' : 'z-40',
      )}
    >
      {/* header */}
      <div
        className={cn('flex h-11 shrink-0 select-none items-center gap-2 border-b border-line pl-4 pr-2', !c.maximized && 'cursor-grab active:cursor-grabbing')}
        onPointerDown={c.maximized ? undefined : floating.onPointerDown}
        onDoubleClick={() => update(c.id, { maximized: !c.maximized })}
        title={c.maximized ? undefined : 'Drag to move · double-click to maximize'}
      >
        <span className="flex-1 truncate text-[13.5px] font-semibold">{title}</span>
        {savedAt && <span className="text-[11px] text-faint">Draft saved</span>}
        <IconButton size="sm" label="Minimize" onClick={() => update(c.id, { minimized: true, maximized: false })}><Minus /></IconButton>
        <IconButton size="sm" label={c.maximized ? 'Restore' : 'Full screen'} onClick={() => update(c.id, { maximized: !c.maximized })}>{c.maximized ? <Minimize2 /> : <Maximize2 />}</IconButton>
        <IconButton size="sm" label="Close (saved as draft)" onClick={() => close(c.id)}><X /></IconButton>
      </div>

      {/* from */}
      {identities.length > 0 && (
        <div className="relative flex h-10 items-center gap-2 border-b border-line px-4">
          <span className="w-10 text-[12.5px] text-faint">From</span>
          <button onClick={() => setFromOpen(!fromOpen)} className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-[13px] hover:bg-line">
            {from && <Favicon domain={domainOf(from)} size={16} />}
            <span>{me?.user.displayName} &lt;{from}&gt;</span>
            {identities.length > 1 && <ChevronDown className="size-3.5 text-faint" />}
          </button>
          <Popover open={fromOpen && identities.length > 1} onClose={() => setFromOpen(false)}>
            {identities.map((a) => (
              <MenuItem key={a.email} icon={<Favicon domain={a.domain} size={16} />} onClick={() => {
                setFromOpen(false);
                // Swap the signature to the new identity's one.
                const oldSig = signatureFor(from);
                const newSig = signatureFor(a.email);
                const html = oldSig !== newSig && editor ? stripSignature(editor.getHTML()) + (newSig ? `<div data-signature="1">${newSig}</div>` : '') : c.html;
                update(c.id, (s) => ({ from: a.email, html, rev: html !== c.html ? s.rev + 1 : s.rev }));
              }}>
                {a.email}
              </MenuItem>
            ))}
          </Popover>
          <span className="flex-1" />
          {!c.showCc && <button className="text-[12px] text-muted hover:text-fg" onClick={() => update(c.id, { showCc: true })}>Cc / Bcc</button>}
        </div>
      )}

      <RecipientInput label="To" value={c.to} onChange={(to) => update(c.id, { to })} autoFocus={!c.to.length && !c.aiFirst && !isReply} />
      {c.showCc && (
        <>
          <RecipientInput label="Cc" value={c.cc} onChange={(cc) => update(c.id, { cc })} exclude={c.to} />
          <RecipientInput label="Bcc" value={c.bcc} onChange={(bcc) => update(c.id, { bcc })} exclude={[...c.to, ...c.cc]} />
        </>
      )}
      <input
        value={c.subject}
        onChange={(e) => update(c.id, { subject: e.target.value })}
        placeholder="Subject"
        className="h-11 shrink-0 border-b border-line bg-transparent px-4 text-[14px] font-medium outline-none placeholder:text-faint"
      />

      {/* AI bar */}
      <div className={cn('ai-ring mx-3 mt-3 shrink-0 rounded-2xl bg-bg/40', busy && 'busy')}>
        <div className="flex items-center gap-2 py-1.5 pl-3 pr-1.5">
          <Sparkles className={cn('size-4 shrink-0 text-accent', busy && 'animate-pulse')} />
          <input
            ref={aiInput}
            value={aiPrompt}
            disabled={busy}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                if (aiPrompt.trim() || isReply) void runAi(aiPrompt.trim() || 'Write the most helpful natural reply.');
              }
            }}
            placeholder={busy ? 'AI is writing…' : isReply ? 'Tell the AI what to say, or just press Enter' : 'Describe the email you want — AI writes it'}
            className="h-8 min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-faint"
          />
          {busy ? (
            <Button size="sm" variant="soft" icon={<Square className="size-3 fill-current" />} onClick={() => abort.current?.abort()}>Stop</Button>
          ) : (
            <>
              {undoHtml !== null && (
                <IconButton size="sm" label="Undo AI" onClick={() => (!c.htmlMode && editor?.commands.setContent(undoHtml, false), update(c.id, (s) => ({ html: undoHtml, rev: s.rev + 1 })), setUndoHtml(null))}><RotateCcw /></IconButton>
              )}
              <IconButton size="sm" label="Design this domain's brand template" onClick={() => setDesignOpen(true)}><Palette /></IconButton>
              <div className="relative">
                <Button size="sm" variant="ghost" icon={<Wand2 className="size-3.5" />} onClick={() => setImproveOpen(!improveOpen)} disabled={!htmlToPlain(stripSignature(c.html)).trim()}>
                  Improve
                </Button>
                <Popover open={improveOpen} onClose={() => setImproveOpen(false)} align="right">
                  {REWRITES.map((r) => (
                    <MenuItem key={r.label} icon={r.icon} onClick={() => (setImproveOpen(false), void runAi('', { mode: 'rewrite', rewriteHow: r.instruction }))}>{r.label}</MenuItem>
                  ))}
                  <MenuItem icon={<Sparkles />} onClick={() => (setImproveOpen(false), void runAi('', { mode: 'continue', rewriteHow: 'continue' }))}>Continue writing</MenuItem>
                </Popover>
              </div>
              <Button size="sm" variant="ai" onClick={() => void runAi(aiPrompt.trim() || 'Write the most helpful natural reply.')} disabled={!aiPrompt.trim() && !isReply}>
                {isReply && !aiPrompt.trim() ? 'Draft reply' : 'Write'}
              </Button>
            </>
          )}
        </div>
        <div className="flex gap-1 overflow-x-auto px-3 pb-2">
          {TONES.map((t) => (
            <button
              key={t}
              onClick={() => setTone(tone === t.toLowerCase() ? null : t.toLowerCase())}
              className={cn('rounded-full px-2.5 py-0.5 text-[11.5px] font-medium transition', tone === t.toLowerCase() ? 'bg-accent text-accent-fg' : 'text-muted hover:bg-line hover:text-fg')}
            >
              {t}
            </button>
          ))}
          {prefs?.ai?.tone && !tone && <span className="ml-1 self-center text-[11px] text-faint">default: {prefs.ai.tone}</span>}
        </div>
      </div>

      {(showToolbar || c.htmlMode) && (
        <div className="mt-2 shrink-0 border-b border-line px-3 py-1.5">
          <EditorToolbar
            editor={editor}
            mode={c.htmlMode ? 'html' : 'rich'}
            onModeChange={(m) =>
              m === 'html'
                ? update(c.id, { htmlMode: true, html: editor?.getHTML() ?? c.html })
                : update(c.id, (s) => ({ htmlMode: false, rev: s.rev + 1 }))
            }
          />
        </div>
      )}

      {c.htmlMode ? (
        <HtmlSourceEditor html={c.html} onChange={(html) => update(c.id, { html })} />
      ) : (
        <RichEditor
          html={c.html}
          rev={c.rev}
          streaming={busy}
          onReady={setEditor}
          onChange={(html) => update(c.id, { html })}
          onRewriteSelection={rewriteSelection}
          placeholder={isReply ? 'Write your reply… or let AI draft it above' : 'Write your message…'}
        />
      )}

      {/* selection rewrite preview */}
      <AnimatePresence>
        {selectionResult && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className={cn('ai-ring mx-3 mb-2 rounded-2xl bg-raised p-3 shadow-xl', selectionResult.busy && 'busy')}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]"><Sparkles className="size-3 text-accent" /><span className="ai-text">Rewrite</span></div>
            <div className="selectable max-h-40 overflow-y-auto whitespace-pre-wrap text-[13.5px]">{selectionResult.text || <span className="shimmer-text">Rewriting…</span>}</div>
            {!selectionResult.busy && (
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelectionResult(null)}>Discard</Button>
                <Button size="sm" variant="primary" onClick={() => {
                  editor?.chain().focus().insertContentAt({ from: selectionResult.from, to: selectionResult.to }, selectionResult.text.includes('\n') ? plainToHtml(selectionResult.text) : selectionResult.text).run();
                  setSelectionResult(null);
                }}>Replace</Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {c.quoteHtml && (
        <label className="mx-5 mb-1 flex shrink-0 cursor-pointer items-center gap-2 text-[12px] text-muted">
          <input type="checkbox" checked={c.includeQuote} onChange={(e) => update(c.id, { includeQuote: e.target.checked })} className="accent-[var(--accent)]" />
          Include {c.mode === 'forward' ? 'forwarded message' : 'quoted original'}
        </label>
      )}

      {c.attachments.length > 0 && (
        <div className="flex shrink-0 flex-wrap gap-1.5 px-4 pb-2">
          {c.attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg/40 py-1 pl-2 pr-1 text-[12px]">
              {a.loading ? <Loader2 className="size-3.5 animate-spin" /> : <Paperclip className="size-3.5 text-muted" />}
              <span className="max-w-[160px] truncate">{a.fileName}</span>
              <span className="text-faint">{formatBytes(a.size)}</span>
              <button className="rounded p-0.5 text-faint hover:bg-line hover:text-fg" onClick={() => update(c.id, (s) => ({ attachments: s.attachments.filter((x) => x.id !== a.id) }))}><X className="size-3" /></button>
            </span>
          ))}
          {c.attachments.reduce((n, a) => n + a.size, 0) > LINK_THRESHOLD && (
            <span className="basis-full text-[11px] text-faint">Too large for some mail servers, so the biggest files will go out as download links that work for a year.</span>
          )}
        </div>
      )}

      {/* footer */}
      <div className="flex h-14 shrink-0 items-center gap-1.5 border-t border-line px-3">
        <Button variant="primary" onClick={send} icon={<Send className="size-4" />} disabled={busy} title="Send (Ctrl+Enter)">Send</Button>
        <IconButton label="Attach files" onClick={() => fileInput.current?.click()}><Paperclip /></IconButton>
        <IconButton label="Formatting & HTML editor" active={showToolbar || c.htmlMode} onClick={() => setShowToolbar(!showToolbar)}><Type /></IconButton>
        <IconButton label={c.htmlMode ? 'Back to visual editor' : 'Edit HTML source'} active={c.htmlMode} onClick={() => (c.htmlMode ? update(c.id, (s) => ({ htmlMode: false, rev: s.rev + 1 })) : update(c.id, { htmlMode: true, html: editor?.getHTML() ?? c.html }))}><CodeXml /></IconButton>
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && void addFiles(e.target.files).then(() => (e.target.value = ''))} />
        {(prefs?.crm?.mode ?? 'off') !== 'off' && (
          <button
            onClick={() => update(c.id, { logToCrm: !c.logToCrm })}
            className={cn('ml-1 inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium transition', c.logToCrm ? 'bg-accent/15 text-accent' : 'text-muted hover:bg-line')}
            title="After sending, AI logs this email on the matching CRM record"
          >
            <Workflow className="size-3.5" />
            Log to CRM
          </button>
        )}
        {template && (
          <span className={cn('ml-1 inline-flex h-8 items-center rounded-lg text-[12px] font-medium', c.brand ? 'bg-accent/15 text-accent' : 'text-muted')}>
            <button onClick={() => update(c.id, { brand: !c.brand })} className="flex h-full items-center gap-1.5 rounded-l-lg pl-2.5 pr-1.5 hover:bg-line" title="Wrap this message in the domain's brand template">
              <span className="size-3 rounded-full" style={{ background: `linear-gradient(135deg, ${template.primaryColor ?? '#888'}, ${template.accentColor ?? '#bbb'})` }} />
              {template.name}
            </button>
            <button onClick={() => setPreviewOpen(true)} className="flex h-full items-center rounded-r-lg px-1.5 hover:bg-line" title="Preview the branded email"><Eye className="size-3.5" /></button>
          </span>
        )}
        <span className="flex-1" />
        <IconButton label="Discard" onClick={() => void discard()}><Trash2 /></IconButton>
      </div>

      <TemplateDesignerModal open={designOpen} onClose={() => setDesignOpen(false)} domain={from?.split('@')[1]} />
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} width={760} title="Branded preview">
        <div className="bg-[#e9e9ef] p-4">
          <iframe title="Branded preview" sandbox="allow-same-origin" className="h-[70vh] w-full rounded-xl border-0 bg-white" srcDoc={brandedHtml({ ...c, from }, c.html + (c.includeQuote && c.quoteHtml ? c.quoteHtml : ''))} />
        </div>
      </Modal>
      <AnimatePresence>
        {dragging && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pointer-events-none absolute inset-2 flex items-center justify-center rounded-2xl border-2 border-dashed border-accent bg-accent/10 text-[14px] font-semibold text-accent">
            Drop files to attach
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
